/**
 * Typed API client backed by the generated OpenAPI schema.
 *
 * Wraps `openapi-fetch` with our existing Bearer-token + base-URL
 * conventions. Resource modules in this directory (`accounts.ts`,
 * `findings.ts`, …) use `apiClient.GET(...)` / `.POST(...)` / etc.
 * and get typed request params + responses directly from
 * `./schema.d.ts` (generated by `npm run gen:api`).
 *
 * Why the unwrap helper:
 * - openapi-fetch returns `{ data, error, response }`. We've always
 *   thrown on non-2xx (the rest of the app catches `ApiError`), so
 *   the small `unwrap` helper preserves that contract.
 *
 * Why the legacy `apiFetch` stays:
 * - Some call paths still use it (multipart uploads, blob downloads,
 *   places that read non-JSON responses). Migration to the typed
 *   client is incremental; deleting `apiFetch` is a follow-up.
 */

import createFetchClient, { type Client } from 'openapi-fetch';

import type { paths } from './schema';

const API_BASE_URL = (import.meta.env.VITE_API_BASE_URL ?? '') as string;

export class ApiError extends Error {
  constructor(
    public status: number,
    public body: unknown,
  ) {
    super(`API ${status}`);
    this.name = 'ApiError';
  }
}

/** Singleton typed client. Per-call Bearer token is passed via
 *  `headers: { Authorization: \`Bearer ${idToken}\` }` on each
 *  `.GET / .POST / etc.` call — auth state lives in React's auth
 *  context, not in the client, so a token rotation doesn't require
 *  rebuilding the client.
 */
export const apiClient: Client<paths> = createFetchClient<paths>({
  baseUrl: API_BASE_URL,
});

/** Convert openapi-fetch's `{ data, error, response }` envelope to the
 *  throw-on-non-2xx contract the rest of the codebase expects.
 *
 *  Resource helpers wrap each call:
 *
 *  ```ts
 *  return unwrap(
 *    await apiClient.GET('/api/customers', { headers: bearer(idToken) })
 *  );
 *  ```
 */
export function unwrap<T>(
  result: {
    data?: T;
    error?: unknown;
    response: Response;
  },
): T {
  if (result.error !== undefined || !result.response.ok) {
    throw new ApiError(result.response.status, result.error ?? null);
  }
  // `data` is the parsed JSON body. `undefined` is only possible on
  // 204 No Content; surface that as `null` for callers that don't
  // expect a body.
  return (result.data ?? null) as T;
}

/** Build the Authorization headers object from an id token. Centralized
 *  so the literal `Bearer ${idToken}` string lives in one place. */
export function bearer(idToken: string | null | undefined): { Authorization?: string } {
  return idToken ? { Authorization: `Bearer ${idToken}` } : {};
}

/** Retry policy for mutations. A 401 means the request was rejected at the
 *  auth layer BEFORE the handler ran, so it had no server-side effect — safe
 *  to retry once. Combined with the token-refresh hardening in AuthContext,
 *  the retry re-runs the mutationFn with a freshly-minted token, healing the
 *  transient 401s that Clerk's session refresh causes mid-work (INP-502).
 *  Deliberately does NOT retry other statuses or network errors, where a
 *  retry could double-apply a non-idempotent write. */
export function shouldRetryMutation(failureCount: number, error: unknown): boolean {
  return failureCount < 1 && error instanceof ApiError && error.status === 401;
}

// ── Legacy fetch wrapper ────────────────────────────────────────────
//
// Pre-codegen helper still used by:
// - multipart uploads (engagementFiles, audit-run create)
// - blob downloads (CSV / PDF exports)
// - any call site not yet migrated to the typed client
//
// Will shrink as call sites migrate; final deletion is a follow-up.

export async function apiFetch<T>(
  path: string,
  init: RequestInit & { idToken?: string | null } = {},
): Promise<T> {
  const { idToken, headers, ...rest } = init;
  const res = await fetch(`${API_BASE_URL}${path}`, {
    ...rest,
    headers: {
      Accept: 'application/json',
      ...(idToken ? { Authorization: `Bearer ${idToken}` } : {}),
      ...(headers ?? {}),
    },
  });
  const text = await res.text();
  const body = text ? safeJson(text) : null;
  if (!res.ok) throw new ApiError(res.status, body);
  return body as T;
}

function safeJson(text: string): unknown {
  try {
    return JSON.parse(text);
  } catch {
    return text;
  }
}
